let _configuredSiteUrl = "";

export function configureSiteUrl(url: string): void {
  _configuredSiteUrl = url.replace(/\/$/, "");
}

function getSiteUrl(): string {
  if (_configuredSiteUrl) return _configuredSiteUrl;
  try {
    // @ts-expect-error import.meta.env only available in Vite environments
    const envUrl = import.meta.env?.VITE_SITE_URL;
    if (envUrl) return envUrl.replace(/\/$/, "");
  } catch {
    // Node or non-Vite environment
  }
  return "";
}

export function canonical(path: string): string {
  const siteUrl = getSiteUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  return siteUrl ? `${siteUrl}${p}` : p;
}

export function webPageSchema(
  title: string,
  description: string,
  path: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonical(path),
  };
}

export function collectionPageSchema(
  title: string,
  description: string,
  path: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url: canonical(path),
  };
}

export function faqPageSchema(
  items: Array<{ question: string; answer: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function articleSchema(
  options: {
    title: string;
    description: string;
    path: string;
    image: string;
    publishedAt: string;
    authorName?: string;
    category?: string;
    tags?: string[];
  }
): Record<string, unknown> {
  const { title, description, path, image, publishedAt, authorName, category, tags } = options;
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image,
    url: canonical(path),
    datePublished: publishedAt,
    dateModified: publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical(path),
    },
    publisher: {
      "@type": "Organization",
      name: "美好時光指南",
      url: getSiteUrl(),
    },
  };
  if (authorName) {
    schema.author = {
      "@type": "Person",
      name: authorName,
      url: canonical("/authors"),
    };
  }
  if (category) {
    schema.articleSection = category;
  }
  if (tags && tags.length > 0) {
    schema.keywords = tags.join(", ");
  }
  return schema;
}

export function breadcrumbSchema(
  items: Array<{ name: string; path: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: canonical(item.path),
    })),
  };
}

export function localBusinessSchema(
  name: string,
  description: string,
  path: string,
  image: string,
  rating?: number,
  reviewCount?: number,
  address?: string
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name,
    description,
    image,
    url: canonical(path),
  };
  if (rating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating,
      reviewCount: reviewCount || 0,
    };
  }
  if (address) {
    schema.address = {
      "@type": "PostalAddress",
      addressLocality: address,
      addressCountry: "TW",
    };
  }
  return schema;
}

export function profilePageSchema(
  name: string,
  bio: string,
  image: string,
  path: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name,
      description: bio,
      image,
      url: canonical(path),
    },
  };
}

export function organizationSchema(logoUrl?: string): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "美好時光指南",
    alternateName: "GoldenTimeGuides",
    url: getSiteUrl(),
    sameAs: [
      "https://www.facebook.com/goldentimeguide",
      "https://www.instagram.com/goldentimeguide",
    ],
  };
  if (logoUrl) {
    schema.logo = logoUrl;
  }
  return schema;
}

export function websiteSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "美好時光指南",
    alternateName: "GoldenTimeGuides",
    url: getSiteUrl(),
  };
}

export function graphSchema(
  schemas: Record<string, unknown>[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": schemas,
  };
}